import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";

import { PRIMARY_BUTTON_SX, SECONDARY_BUTTON_SX } from "../../theme/buttonstyles";
import { BRAND_BLUE, BRAND_GREEN } from "../../theme/colors";

const EMPTY_MEMBER = {
  name: "",
  member_type: "",
  status: "active",
  sector: "",
  phone_number: "",
  email: "",
  website: "",
  address: "",
  city: "",
  state: "",
  county: "",
  zip_code: "",
  country: "",
  description: "",
  joined_at: "",
};

const STATUS_OPTIONS = ["active", "prospect", "lapsed", "suspended", "cancelled"];
const MEMBER_TYPE_OPTIONS = ["Business", "Nonprofit", "Government", "Individual", "Investor", "Partner"];

const FIELD_SX = {
  "& .MuiOutlinedInput-root": {
    color: BRAND_BLUE,
    background: "#ffffff",
    "& fieldset": { border: "1px solid #cbd5e1" },
    "&:hover fieldset": { borderColor: BRAND_BLUE },
    "&.Mui-focused fieldset": { borderColor: BRAND_BLUE, borderWidth: 2 },
  },
  "& .MuiInputLabel-root, & .MuiInputLabel-root.Mui-focused": {
    color: "#64748b",
    fontWeight: 600,
  },
};

// MUI v7's Grid dropped the item/xs/sm/md breakpoint props in favor of `size`;
// a plain CSS grid avoids relying on that API and keeps spans explicit.
const FORM_ROW_SX = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", sm: "repeat(12, 1fr)" },
  gap: "16px",
};

function Field({ span, children }) {
  return <Box sx={{ gridColumn: { xs: "span 1", sm: `span ${span}` } }}>{children}</Box>;
}

function toFormValue(member) {
  return {
    ...EMPTY_MEMBER,
    ...Object.fromEntries(
      Object.entries(member || {}).map(([key, value]) => [key, value ?? ""]),
    ),
  };
}

function cleanPayload(values, isEdit) {
  const payload = {};
  Object.entries(values).forEach(([key, value]) => {
    const nextValue = typeof value === "string" ? value.trim() : value;
    if (isEdit && nextValue === "") {
      payload[key] = null;
    } else if (nextValue !== "") {
      payload[key] = nextValue;
    }
  });
  return payload;
}

function SectionHeader({ children }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2 }}>
      <Box sx={{ width: 4, height: 22, borderRadius: 2, bgcolor: BRAND_GREEN }} />
      <Typography sx={{ color: BRAND_BLUE, fontWeight: 800 }}>{children}</Typography>
    </Box>
  );
}

export default function MembershipMemberDialog({
  open,
  member,
  loading = false,
  saving = false,
  onClose,
  onSubmit,
}) {
  const isEdit = Boolean(member?.id);
  const [values, setValues] = useState(EMPTY_MEMBER);

  useEffect(() => {
    if (open) setValues(toFormValue(member));
  }, [member, open]);

  const title = useMemo(
    () => (isEdit ? "Edit Member" : "Add Member"),
    [isEdit],
  );

  const handleChange = (field) => (event) => {
    setValues((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleSubmit = () => {
    if (!values.name.trim()) return;
    onSubmit(cleanPayload(values, isEdit));
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 3,
          border: `1px solid ${BRAND_BLUE}`,
          boxShadow: "0 18px 54px rgba(0,51,108,0.20)",
        },
      }}
    >
      <DialogTitle sx={{ color: BRAND_BLUE, fontWeight: 800 }}>
        {title}
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: "#fbfcf8" }}>
        {loading ? (
          <Typography sx={{ color: BRAND_BLUE, fontWeight: 700 }}>Loading...</Typography>
        ) : (
          <Box sx={{ pt: 1 }}>
            <SectionHeader>Member Details</SectionHeader>
            <Box sx={FORM_ROW_SX}>
              <Field span={8}>
                <TextField
                  label="Organization Name"
                  value={values.name}
                  onChange={handleChange("name")}
                  fullWidth
                  required
                  sx={FIELD_SX}
                />
              </Field>
              <Field span={4}>
                <TextField
                  select
                  label="Status"
                  value={values.status}
                  onChange={handleChange("status")}
                  fullWidth
                  sx={FIELD_SX}
                >
                  {STATUS_OPTIONS.map((option) => (
                    <MenuItem key={option} value={option}>{option}</MenuItem>
                  ))}
                </TextField>
              </Field>
              <Field span={6}>
                <TextField
                  select
                  label="Member Type"
                  value={values.member_type}
                  onChange={handleChange("member_type")}
                  fullWidth
                  sx={FIELD_SX}
                >
                  <MenuItem value="">None</MenuItem>
                  {MEMBER_TYPE_OPTIONS.map((option) => (
                    <MenuItem key={option} value={option}>{option}</MenuItem>
                  ))}
                </TextField>
              </Field>
              <Field span={6}>
                <TextField
                  label="Joined At"
                  type="date"
                  value={values.joined_at}
                  onChange={handleChange("joined_at")}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={FIELD_SX}
                />
              </Field>
              <Field span={12}>
                <TextField
                  label="Sector"
                  value={values.sector}
                  onChange={handleChange("sector")}
                  fullWidth
                  sx={FIELD_SX}
                />
              </Field>
              <Field span={12}>
                <TextField
                  label="Description"
                  value={values.description}
                  onChange={handleChange("description")}
                  fullWidth
                  multiline
                  minRows={3}
                  sx={FIELD_SX}
                />
              </Field>
            </Box>

            <Box sx={{ mt: 3 }}>
              <SectionHeader>Contact & Location</SectionHeader>
              <Box sx={FORM_ROW_SX}>
                <Field span={4}>
                  <TextField label="Email" value={values.email} onChange={handleChange("email")} fullWidth sx={FIELD_SX} />
                </Field>
                <Field span={4}>
                  <TextField label="Phone" value={values.phone_number} onChange={handleChange("phone_number")} fullWidth sx={FIELD_SX} />
                </Field>
                <Field span={4}>
                  <TextField label="Website" value={values.website} onChange={handleChange("website")} fullWidth sx={FIELD_SX} />
                </Field>
                <Field span={12}>
                  <TextField label="Address" value={values.address} onChange={handleChange("address")} fullWidth sx={FIELD_SX} />
                </Field>
                <Field span={3}>
                  <TextField label="City" value={values.city} onChange={handleChange("city")} fullWidth sx={FIELD_SX} />
                </Field>
                <Field span={3}>
                  <TextField label="State" value={values.state} onChange={handleChange("state")} fullWidth sx={FIELD_SX} />
                </Field>
                <Field span={3}>
                  <TextField label="County" value={values.county} onChange={handleChange("county")} fullWidth sx={FIELD_SX} />
                </Field>
                <Field span={3}>
                  <TextField label="ZIP" value={values.zip_code} onChange={handleChange("zip_code")} fullWidth sx={FIELD_SX} />
                </Field>
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving} sx={SECONDARY_BUTTON_SX}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={saving || loading || !values.name.trim()}
          sx={PRIMARY_BUTTON_SX}
        >
          {saving ? "Saving..." : "Save Member"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
